import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../../helpers/firebase'
import { useAuth } from '../../contexts/AuthContext'
import {
  clearParentInviteToken,
  rememberParentInviteToken
} from '../../helpers/parentInviteStorage'
import { acceptedBillingUrl, billingNavigation } from './parentBillingDisplay'
import '../Home.css'
import './ParentInvitePanel.css'

const STATUS_COPY = {
  expired: 'This link expired. Ask the student for a new one.',
  used: 'This link was already used.',
  revoked: 'This link is not valid. Ask the student for a new one.',
  invalid: 'This link is not valid. Ask the student for a new one.'
}

const LINK_NOT_FINISHED = 'Link not finished.'
const LINK_FINISHING = 'Finishing the link...'
const LINK_SUPPORT = 'Payment received but the link is not finished. Contact support@quillnote.app.'
const PAYMENT_ERROR = 'Could not open payment. Try again.'
const LINK_STATUS_TIMEOUT_MS = 20000
const LINK_STATUS_INTERVAL_MS = 2000
const CHECKOUT_SESSION_RE = /^cs_[A-Za-z0-9_]{8,255}$/

let cachedPreviewToken = ''
let cachedPreview = null
let previewRequestToken = ''
let previewRequest = null

export const clearParentInvitePreviewCache = () => {
  cachedPreviewToken = ''
  cachedPreview = null
  previewRequestToken = ''
  previewRequest = null
}

export const readParentInvitePreview = (token) => (
  token && token === cachedPreviewToken ? cachedPreview : null
)

// One preview per token, shared by the dashboard (while the name modal is
// open) and the link form (when that modal closes). A failed read is not
// cached, so the form can try again.
export const prefetchParentInvitePreview = (token) => {
  if (!token) return Promise.resolve({ status: 'invalid' })
  if (token === cachedPreviewToken && cachedPreview) return Promise.resolve(cachedPreview)
  if (token === previewRequestToken && previewRequest) return previewRequest
  const request = (async () => {
    const previewParentInvite = httpsCallable(functions, 'previewParentInvite')
    const result = await previewParentInvite({ token })
    const data = result && result.data ? result.data : { status: 'invalid' }
    cachedPreviewToken = token
    cachedPreview = data
    return data
  })()
  previewRequestToken = token
  previewRequest = request
  const finish = () => {
    if (previewRequest === request) {
      previewRequest = null
      previewRequestToken = ''
    }
  }
  request.then(finish, finish)
  return request
}

const callableMessage = (error, fallback) => {
  const message = typeof error?.message === 'string' ? error.message.trim() : ''
  return message || fallback
}

// cancel() drops the timer after the status call wins. finish() also settles
// the promise so leaving the page does not leave the poll awaiting a timer.
const delay = (ms) => {
  let timer
  let settle
  const promise = new Promise((resolve) => {
    settle = resolve
    timer = setTimeout(resolve, ms)
  })
  promise.cancel = () => {
    clearTimeout(timer)
  }
  promise.finish = () => {
    clearTimeout(timer)
    settle()
  }
  return promise
}

const fatalStatusCode = (code) => (
  code.includes('permission-denied') ||
  code.includes('invalid-argument') ||
  code.includes('unauthenticated') ||
  code.includes('not-found')
)

// Optional first name plus payment. Blank is allowed. Checkout is the only
// way this screen links the student.
export const ParentInviteLinkForm = ({ token, onLinked, onUnavailable, checkoutReturn = null }) => {
  const history = useHistory()
  const { currentUser, userProfile, logout } = useAuth()
  const [preview, setPreview] = useState(() => readParentInvitePreview(token))
  const [loading, setLoading] = useState(() => !readParentInvitePreview(token))
  const [firstName, setFirstName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [linkFinish, setLinkFinish] = useState('idle')
  const [resumedSessionId, setResumedSessionId] = useState('')
  const onLinkedRef = useRef(onLinked)
  onLinkedRef.current = onLinked

  const role = userProfile?.role
  const parentReady = !!currentUser && role === 'parent' && userProfile?.pendingParentName !== true
  const checkoutBilling = resumedSessionId
    ? 'success'
    : (checkoutReturn && checkoutReturn.billing)
  const returnedSessionId = checkoutReturn && typeof checkoutReturn.sessionId === 'string'
    ? checkoutReturn.sessionId
    : ''
  const checkoutSessionId = resumedSessionId || returnedSessionId

  useEffect(() => {
    let cancelled = false
    const cached = readParentInvitePreview(token)
    if (cached) {
      setPreview(cached)
      setLoading(false)
      setError('')
      return undefined
    }
    const load = async () => {
      setLoading(true)
      setError('')
      try {
        const data = await prefetchParentInvitePreview(token)
        if (!cancelled) setPreview(data || { status: 'invalid' })
      } catch (previewError) {
        if (!cancelled) {
          setPreview({ status: 'invalid' })
          setError(callableMessage(previewError, STATUS_COPY.invalid))
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    if (token) load()
    else {
      setPreview({ status: 'invalid' })
      setLoading(false)
    }
    return () => {
      cancelled = true
    }
  }, [token])

  const notifiedUnavailable = useRef(false)
  useEffect(() => {
    notifiedUnavailable.current = false
  }, [token])
  useEffect(() => {
    if (!preview || preview.status === 'active' || loading) return
    if (checkoutBilling === 'success' && parentReady) return
    if (notifiedUnavailable.current) return
    notifiedUnavailable.current = true
    if (onUnavailable) onUnavailable(preview.status)
  }, [preview, loading, onUnavailable, checkoutBilling, parentReady])

  useEffect(() => {
    if (!parentReady || checkoutBilling !== 'success') return undefined
    if (!CHECKOUT_SESSION_RE.test(checkoutSessionId)) {
      setLinkFinish('failed')
      return undefined
    }
    let active = true
    let pendingDelay = null
    setLinkFinish('pending')
    const deadline = Date.now() + LINK_STATUS_TIMEOUT_MS
    const finishFailed = () => {
      if (active) setLinkFinish('failed')
    }
    const wait = (ms) => {
      const pending = delay(ms)
      pendingDelay = pending
      return pending
    }
    const run = async () => {
      while (active) {
        const left = deadline - Date.now()
        if (left <= 0) {
          finishFailed()
          return
        }
        let status = ''
        const budget = wait(left)
        try {
          const getLinkCheckoutStatus = httpsCallable(functions, 'getLinkCheckoutStatus')
          const result = await Promise.race([
            getLinkCheckoutStatus({ sessionId: checkoutSessionId }).then((value) => {
              budget.cancel()
              return value
            }),
            budget.then(() => ({ timedOut: true }))
          ])
          if (!active) return
          if (result && result.timedOut) {
            finishFailed()
            return
          }
          status = result && result.data ? result.data.status : ''
        } catch (statusError) {
          budget.cancel()
          if (!active) return
          const code = typeof statusError?.code === 'string' ? statusError.code : ''
          if (fatalStatusCode(code)) {
            finishFailed()
            return
          }
          status = 'pending'
        }
        if (!active) return
        if (status === 'linked') {
          clearParentInviteToken()
          if (onLinkedRef.current) await onLinkedRef.current()
          return
        }
        if (status === 'failed') {
          finishFailed()
          return
        }
        const pause = Math.min(LINK_STATUS_INTERVAL_MS, deadline - Date.now())
        if (pause <= 0) {
          finishFailed()
          return
        }
        await wait(pause)
      }
    }
    run()
    return () => {
      active = false
      if (pendingDelay) pendingDelay.finish()
    }
  }, [parentReady, checkoutBilling, checkoutSessionId])

  const payAndLink = async (event) => {
    event.preventDefault()
    if (submitting) return
    setSubmitting(true)
    setError('')
    try {
      const createStudentLinkCheckout = httpsCallable(functions, 'createStudentLinkCheckout')
      const result = await createStudentLinkCheckout({ token, firstName })
      const url = acceptedBillingUrl(result && result.data && result.data.url)
      if (!url) {
        setError(PAYMENT_ERROR)
        setSubmitting(false)
        return
      }
      billingNavigation.assign(url)
    } catch (payError) {
      const reason = payError?.details?.reason
      const paidSessionId = payError?.details?.sessionId
      if (reason === 'already_paid' && CHECKOUT_SESSION_RE.test(paidSessionId || '')) {
        setResumedSessionId(paidSessionId)
        setError('')
        setSubmitting(false)
        return
      }
      if (reason === 'parent_name_required') {
        rememberParentInviteToken(token)
        history.push('/parent/dashboard')
        return
      }
      const code = typeof payError?.code === 'string' ? payError.code : ''
      console.error('Link checkout failed', code)
      setError(callableMessage(payError, PAYMENT_ERROR))
      setSubmitting(false)
    }
  }

  if (parentReady && checkoutBilling === 'success') {
    const sessionOk = CHECKOUT_SESSION_RE.test(checkoutSessionId)
    if (linkFinish === 'failed' || !sessionOk) {
      return (
        <p className='parent-invite-copy' role='alert'>
          {LINK_SUPPORT}
        </p>
      )
    }
    return <p className='parent-invite-status' role='status'>{LINK_FINISHING}</p>
  }

  if (loading) {
    return <p className='parent-invite-status'>Checking this link...</p>
  }

  if (!preview || preview.status !== 'active') {
    return (
      <p className='parent-invite-copy' role='status'>
        {error || STATUS_COPY[preview?.status] || STATUS_COPY.invalid}
      </p>
    )
  }

  const username = preview.studentUsername ? `@${preview.studentUsername}` : 'this student'
  const linkTitle = preview.studentUsername ? `Link @${preview.studentUsername}` : 'Link this student'

  if (!currentUser) {
    return (
      <div className='parent-invite-panel'>
        <h2 className='parent-invite-title'>Link {username} to your parent account.</h2>
        <button
          type='button'
          className='parent-invite-primary'
          onClick={() => {
            rememberParentInviteToken(token)
            history.push(`/?signup=parent&invite=${encodeURIComponent(token)}`)
          }}
        >
          Create a parent account
        </button>
        <button
          type='button'
          className='parent-invite-secondary'
          onClick={() => {
            rememberParentInviteToken(token)
            history.push(`/?invite=${encodeURIComponent(token)}`)
          }}
        >
          I already have an account
        </button>
      </div>
    )
  }

  if (role === 'student') {
    return (
      <div className='parent-invite-panel'>
        <p className='parent-invite-copy'>
          Sign out and open this link with a parent account.
        </p>
        <button type='button' className='parent-invite-primary' onClick={() => logout()}>
          Sign out
        </button>
      </div>
    )
  }

  if (role !== 'parent') {
    return (
      <p className='parent-invite-copy'>
        Open this link with a parent account.
      </p>
    )
  }

  if (userProfile?.pendingParentName === true) {
    return (
      <div className='parent-invite-panel'>
        <p className='parent-invite-copy'>
          Add your name first. Then you can link {username}.
        </p>
        <button
          type='button'
          className='parent-invite-primary'
          onClick={() => {
            rememberParentInviteToken(token)
            history.push('/parent/dashboard')
          }}
        >
          Continue
        </button>
      </div>
    )
  }

  return (
    <form className='parent-invite-panel' onSubmit={payAndLink}>
      <h2 className='parent-invite-title'>{linkTitle}</h2>
      <p className='parent-invite-copy'>See what they're stuck on, and when a lesson clicks.</p>
      <p className='parent-invite-copy'>$9.99/month plus tax for unlimited.</p>
      {checkoutBilling === 'cancel' && (
        <p className='parent-invite-copy' role='status'>{LINK_NOT_FINISHED}</p>
      )}
      <label className='parent-invite-field' htmlFor='student-first-name'>
        Student's first name
      </label>
      <input
        id='student-first-name'
        className='signin-input'
        type='text'
        value={firstName}
        onChange={(event) => setFirstName(event.target.value)}
        autoComplete='off'
        disabled={submitting}
        style={{ fontSize: 16 }}
      />
      <p className='parent-invite-copy'>You can add this later.</p>
      {error && <p className='parent-invite-error' role='alert'>{error}</p>}
      <button type='submit' className='parent-invite-primary' disabled={submitting}>
        Pay and link
      </button>
    </form>
  )
}

const ParentInviteClaim = () => {
  const { token } = useParams()
  const history = useHistory()
  const location = useLocation()

  useEffect(() => {
    rememberParentInviteToken(token)
  }, [token])

  const params = new URLSearchParams(location.search || '')
  const billing = params.get('billing')
  const sessionId = params.get('session_id') || ''
  const checkoutReturn = billing === 'success' || billing === 'cancel'
    ? { billing, sessionId }
    : null

  return (
    <div className='home-container dynamic-bg'>
      <div className='home-layout'>
        <div className='signin-card'>
          <ParentInviteLinkForm
            token={token || ''}
            checkoutReturn={checkoutReturn}
            onLinked={() => history.replace('/parent/dashboard')}
          />
        </div>
      </div>
    </div>
  )
}

export default ParentInviteClaim
